import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  BookOpen,
  Search,
  Loader2,
  Edit,
  Rocket,
  AlertCircle,
} from "lucide-react";

import adminService from "@/services/adminService";

const STAGE_COLORS = {
  PENDING: "bg-slate-500/20 text-slate-300",
  RECORDING_IN_PROGRESS: "bg-purple-500/20 text-purple-300",
  EDITING: "bg-blue-500/20 text-blue-300",
  QC: "bg-yellow-500/20 text-yellow-300",
  COMPLETED: "bg-green-500/20 text-green-300",
};

export default function AdminBooks() {
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBook, setSelectedBook] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);

  // Pipeline edit state
  const [editProgress, setEditProgress] = useState({
    recording_pct: 0,
    editing_pct: 0,
    verification_pct: 0,
    qc_pct: 0,
    encryption_pct: 0,
    launched: false,
  });

  useEffect(() => {
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBooks();
      setBooks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading books:", error);
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (book) => {
    setSelectedBook(book);
    setEditProgress({
      recording_pct: book.progress?.recording_pct || 0,
      editing_pct: book.progress?.editing_pct || 0,
      verification_pct: book.progress?.verification_pct || 0,
      qc_pct: book.progress?.qc_pct || 0,
      encryption_pct: book.progress?.encryption_pct || 0,
      launched: book.progress?.launched || false,
    });
    setShowDrawer(true);
  };

  const checkCriticalChange = (field, value) => {
    const criticalFields = ["verification_pct", "qc_pct", "encryption_pct"];
    if (criticalFields.includes(field) && value === 100) return true;
    if (field === "launched" && value === true) return true;
    return false;
  };

  const handlePipelineUpdate = async (field, value) => {
    // Check if critical change requires reason
    if (checkCriticalChange(field, value)) {
      setPendingUpdate({ field, value });
      setShowReasonModal(true);
      return;
    }

    // Direct update for non-critical changes
    await submitPipelineUpdate({ [field]: value });
  };

  const submitPipelineUpdate = async (updates, reasonText = null) => {
    try {
      setProcessing(true);
      await adminService.updatePipeline(selectedBook.id, {
        ...updates,
        reason: reasonText,
      });
      toast.success("Pipeline updated");
      await loadBooks();
      
      // Update local state
      const updatedBook = books.find((b) => b.id === selectedBook.id);
      if (updatedBook) {
        setSelectedBook({ ...selectedBook, progress: { ...selectedBook.progress, ...updates } });
        setEditProgress({ ...editProgress, ...updates });
      }
    } catch (error) {
      console.error("Error updating pipeline:", error);
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmCriticalUpdate = async () => {
    if (!reason || reason.length < 5) {
      toast.error("Reason required (min 5 characters)");
      return;
    }

    await submitPipelineUpdate({ [pendingUpdate.field]: pendingUpdate.value }, reason);
    setShowReasonModal(false);
    setReason("");
    setPendingUpdate(null);
  };

  const filteredBooks = books.filter(
    (book) =>
      book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-books">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Books</h1>
          <p className="text-slate-400">Manage book pipeline and progress</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search books..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      {/* Books Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBooks.map((book) => (
          <Card
            key={book.id}
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => handleOpenDrawer(book)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate">{book.title}</h3>
                  <p className="text-sm text-slate-400">{book.author}</p>
                </div>
                <Badge className={STAGE_COLORS[book.recording_status] || STAGE_COLORS.PENDING}>
                  {book.recording_status || "PENDING"}
                </Badge>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                <span>{book.language}</span>
                <span>•</span>
                <span>{book.genre}</span>
              </div>

              {/* Progress Bars */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Recording</span>
                  <span className="text-purple-400">{book.progress?.recording_pct || 0}%</span>
                </div>
                <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all"
                    style={{ width: `${book.progress?.recording_pct || 0}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Overall</span>
                  <span className="text-green-400">
                    {Math.round(
                      ((book.progress?.recording_pct || 0) +
                        (book.progress?.editing_pct || 0) +
                        (book.progress?.qc_pct || 0)) /
                        3
                    )}%
                  </span>
                </div>
              </div>

              {book.progress?.launched && (
                <div className="mt-3 flex items-center gap-1 text-green-400 text-xs">
                  <Rocket className="w-3 h-3" />
                  <span>Launched</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredBooks.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No books found</p>
          </CardContent>
        </Card>
      )}

      {/* Book Detail Drawer */}
      <Sheet open={showDrawer} onOpenChange={setShowDrawer}>
        <SheetContent className="bg-slate-800 border-slate-700 text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">{selectedBook?.title}</SheetTitle>
          </SheetHeader>

          {selectedBook && (
            <div className="space-y-6 mt-6">
              {/* Book Info */}
              <div className="space-y-2 text-sm">
                <p className="text-slate-400">Author: <span className="text-white">{selectedBook.author}</span></p>
                <p className="text-slate-400">Language: <span className="text-white">{selectedBook.language}</span></p>
                <p className="text-slate-400">Genre: <span className="text-white">{selectedBook.genre}</span></p>
              </div>

              {/* Pipeline Editor */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Pipeline Control
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Recording */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label>Recording</Label>
                      <span className="text-purple-400">{editProgress.recording_pct}%</span>
                    </div>
                    <Slider
                      value={[editProgress.recording_pct]}
                      onValueChange={([v]) => {
                        setEditProgress({ ...editProgress, recording_pct: v });
                      }}
                      onValueCommit={([v]) => handlePipelineUpdate("recording_pct", v)}
                      max={100}
                      step={5}
                      className="[&_[role=slider]]:bg-purple-500"
                    />
                  </div>

                  {/* Editing */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label>Editing</Label>
                      <span className="text-blue-400">{editProgress.editing_pct}%</span>
                    </div>
                    <Slider
                      value={[editProgress.editing_pct]}
                      onValueChange={([v]) => {
                        setEditProgress({ ...editProgress, editing_pct: v });
                      }}
                      onValueCommit={([v]) => handlePipelineUpdate("editing_pct", v)}
                      max={100}
                      step={5}
                      className="[&_[role=slider]]:bg-blue-500"
                    />
                  </div>

                  {/* Verification */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label className="flex items-center gap-1">
                        Verification
                        <AlertCircle className="w-3 h-3 text-yellow-500" />
                      </Label>
                      <span className="text-yellow-400">{editProgress.verification_pct}%</span>
                    </div>
                    <Slider
                      value={[editProgress.verification_pct]}
                      onValueChange={([v]) => {
                        setEditProgress({ ...editProgress, verification_pct: v });
                      }}
                      onValueCommit={([v]) => handlePipelineUpdate("verification_pct", v)}
                      max={100}
                      step={5}
                      className="[&_[role=slider]]:bg-yellow-500"
                    />
                  </div>

                  {/* QC */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label className="flex items-center gap-1">
                        QC
                        <AlertCircle className="w-3 h-3 text-yellow-500" />
                      </Label>
                      <span className="text-orange-400">{editProgress.qc_pct}%</span>
                    </div>
                    <Slider
                      value={[editProgress.qc_pct]}
                      onValueChange={([v]) => {
                        setEditProgress({ ...editProgress, qc_pct: v });
                      }}
                      onValueCommit={([v]) => handlePipelineUpdate("qc_pct", v)}
                      max={100}
                      step={5}
                      className="[&_[role=slider]]:bg-orange-500"
                    />
                  </div>

                  {/* Encryption */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <Label className="flex items-center gap-1">
                        Encryption
                        <AlertCircle className="w-3 h-3 text-yellow-500" />
                      </Label>
                      <span className="text-pink-400">{editProgress.encryption_pct}%</span>
                    </div>
                    <Slider
                      value={[editProgress.encryption_pct]}
                      onValueChange={([v]) => {
                        setEditProgress({ ...editProgress, encryption_pct: v });
                      }}
                      onValueCommit={([v]) => handlePipelineUpdate("encryption_pct", v)}
                      max={100}
                      step={5}
                      className="[&_[role=slider]]:bg-pink-500"
                    />
                  </div>

                  {/* Launched Toggle */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-600">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-green-400" />
                      <Label>Launched</Label>
                      <AlertCircle className="w-3 h-3 text-yellow-500" />
                    </div>
                    <Switch
                      checked={editProgress.launched}
                      onCheckedChange={(v) => {
                        setEditProgress({ ...editProgress, launched: v });
                        handlePipelineUpdate("launched", v);
                      }}
                    />
                  </div>

                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-yellow-500" />
                    Fields marked require reason when set to 100% or true
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Reason Required Modal */}
      <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Reason Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              This is a critical pipeline change. Please provide a reason for audit purposes.
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
              className="bg-slate-700 border-slate-600"
              rows={3}
            />
            <div className="flex gap-3">
              <Button
                onClick={handleConfirmCriticalUpdate}
                disabled={processing || reason.length < 5}
                className="flex-1 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
              >
                {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirm
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowReasonModal(false);
                  setReason("");
                  setPendingUpdate(null);
                }}
                className="flex-1 border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
